import { useState, useEffect } from "react";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { performScrape, API_URL } from "../services/api";
import { useScrape } from "../hooks/useScrape";
import { LuSearch, LuMail, LuUsers, LuUserPlus, LuZap, LuShield, LuClock, LuCheck, LuHash, LuAtSign } from "react-icons/lu";

type ScrapeGoal = "emails" | "profiles" | "list";

interface ScrapeFormValues {
    target: string;
    resultLimit: number;
    scrapeType: "following" | "followers";
    locationSelect: string;
    locationCustom?: string;
}

interface ScrapePrice {
    id: string;
    action: string;
    label: string;
    costPerUnit: number;
    unitName: string;
}

const GOALS: { id: ScrapeGoal; label: string; desc: string; action: string; icon: React.ReactNode; color: string; bg: string; border: string; limitLabel: string; limitPlaceholder: string; backendGoal: string }[] = [
    { id: "emails",   label: "Email Discovery",       desc: "Find emails from bios & public fields", action: "email_lookup",   icon: <LuMail size={20} />,   color: "text-violet-600", bg: "bg-violet-50",   border: "border-violet-500", limitLabel: "Emails to Find",      limitPlaceholder: "e.g. 50", backendGoal: "email" },
    { id: "profiles", label: "Profile Details",        desc: "Collect full profile data & bios",     action: "profile_details",icon: <LuAtSign size={20} />,color: "text-blue-600",   bg: "bg-blue-50",    border: "border-blue-500",   limitLabel: "Profiles to Collect",      limitPlaceholder: "e.g. 100", backendGoal: "profile" },
    { id: "list",     label: "Extract Target's Network",  desc: "Get the raw follower/following list",           action: "list_fetch",     icon: <LuUsers size={20} />, color: "text-emerald-600",bg: "bg-emerald-50",  border: "border-emerald-500",limitLabel: "Max Users to Fetch",       limitPlaceholder: "e.g. 500", backendGoal: "list" },
];

// Detect the user's own location from the browser (locale first, timezone as a
// fallback) and map it to a proxy/account group. This is the DEFAULT for the
// location selector; the user can override it in Step 5. Returns one of the
// supported group codes ('in' | 'us' | 'uk').
function detectUserLocation(): string {
    try {
        const country = (navigator.language || "").split("-")[1]?.toUpperCase() || "";
        if (country === "IN") return "in";
        if (country === "GB" || country === "UK") return "uk";
        if (country === "US") return "us";

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "in";
        if (tz === "Europe/London") return "uk";
        if (tz.startsWith("America/")) return "us";
        return "us"; // sensible global default
    } catch {
        return "in";
    }
}

export default function NewScrape() {
    const { user, updateUser } = useAuth();
    const { fetchJobs } = useScrape();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pricing, setPricing] = useState<ScrapePrice[]>([]);
    const [pricingLoading, setPricingLoading] = useState(true);
    const [goal, setGoal] = useState<ScrapeGoal>("emails");
    const { showToast } = useToast();
    const navigate = useNavigate();


    // Default to India (the provisioned proxy region). detectUserLocation() can suggest
    // the user's own region, but we only auto-pick it if it's one you actually have
    // proxies for (VITE_AVAILABLE_REGIONS) — otherwise India, so a scrape never stalls
    // on an unprovisioned exit host. Step 5 still lets the user override manually.
    const availableRegions = (import.meta.env.VITE_AVAILABLE_REGIONS || "in")
        .split(",").map((s: string) => s.trim().toLowerCase()).filter(Boolean);
    const detected = detectUserLocation();
    const detectedLocation = availableRegions.includes(detected) ? detected : "in";
    const { register, handleSubmit, formState: { errors }, control } = useForm<ScrapeFormValues>({ defaultValues: { scrapeType: "followers", locationSelect: detectedLocation, locationCustom: "" } });
    const targetValue = useWatch({ control, name: "target" }) as string | undefined;
    const limitValue = useWatch({ control, name: "resultLimit" }) as number | undefined;
    const scrapeTypeValue = useWatch({ control, name: "scrapeType" }) as "following" | "followers" | undefined;

    const isHashtag = (targetValue || "").trim().startsWith("#");
    const selectedGoal = GOALS.find(g => g.id === goal)!;

    const fetchPricing = async () => {
        setPricingLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/credits/scrape-pricing`);
            const data = await res.json();
            if (data.success) setPricing(data.data);
        } catch { /* silent */ }
        finally { setPricingLoading(false); }
    };

    useEffect(() => { fetchPricing(); }, []);

    const getPrice = (action: string) => {
        const p = pricing.find(p => p.action === action);
        return p ? Number(p.costPerUnit) : 0;
    };



    const onSubmit: SubmitHandler<ScrapeFormValues> = async (data) => {
        setIsSubmitting(true);
        
        // We use a local constant to ensure the current state value is captured correctly
        const activeGoal: ScrapeGoal = goal;
        const limit: number = data.resultLimit;
        const targetType = data.target.trim().startsWith("#") ? "hashtag" : "username";
        const finalLocation = data.locationSelect;
        
        console.log(`[Frontend] Starting ${activeGoal} scrape for ${data.target} (Type: ${targetType}) with limit ${limit} (Location: ${finalLocation})`);

        try {
            const response = await performScrape({
                target: data.target.trim().replace(/^@#/, "#"),
                emailLimit: limit,
                resultLimit: limit,
                scrapeType: data.scrapeType || "followers",
                scrapeGoal: activeGoal,
                scrape_goal: selectedGoal.backendGoal,
                target_type: targetType,
                location: finalLocation,
            });
            if ((response as any).creditsRemaining !== undefined) updateUser({ creditsRemaining: (response as any).creditsRemaining });
            showToast({ title: "Scraping Started", message: "Your job is running. Redirecting to history...", variant: "success", duration: 4000 });
            
            // Eager initial refetch: cache hits complete in <1s so we do a fast
            // follow-up fetch (2s after submit) before navigating to history.
            // This ensures the job shows as "completed" immediately instead of
            // flickering to "in-progress" for one 5-second poll cycle.
            setTimeout(() => fetchJobs(true), 2000);
            
            setIsSubmitting(false);
            navigate("/history");
        } catch (error) {
            setIsSubmitting(false);
            showToast({ title: "Scraping Failed", message: error instanceof Error ? error.message : "An error occurred", variant: "error", duration: 5000 });
        }
    };


    return (
        <div className="p-4 md:p-8 bg-purple-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-200">
                        <LuSearch className="text-white" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">New Scrape</h1>
                        <p className="text-gray-500 text-sm mt-0.5">Extract Instagram data from any profile's network</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                    {/* LEFT */}
                    <div className="lg:col-span-7 flex flex-col gap-6">
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 overflow-hidden">
                            <div className="px-8 pt-8 pb-5 border-b border-gray-50">
                                <h2 className="text-lg font-bold text-gray-900">Configure Scrape Job</h2>
                                <p className="text-sm text-gray-400 mt-1">Choose what you want to collect, set your target, and we'll handle the rest.</p>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8">

                                {/* STEP 1: What to scrape */}
                                <div>
                                    <label className="text-sm font-bold text-gray-800 mb-3 block uppercase tracking-wide">
                                        Step 1 — What do you want to collect?
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {GOALS.map(g => (
                                            <button key={g.id} type="button" onClick={() => setGoal(g.id)}
                                                className={`flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all
                                                    ${goal === g.id ? `${g.border} ${g.bg}` : "border-gray-200 bg-gray-50/50 hover:border-gray-300"}`}>
                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors
                                                    ${goal === g.id ? "bg-white shadow-sm" : "bg-white border border-gray-200"}`}>
                                                    <span className={goal === g.id ? g.color : "text-gray-400"}>{g.icon}</span>
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-bold ${goal === g.id ? g.color : "text-gray-700"}`}>{g.label}</p>
                                                    <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{g.desc}</p>
                                                </div>
                    {!pricingLoading && (
                                                    <div className="text-[11px] font-bold text-blue-600 bg-white px-2 py-0.5 rounded-md shadow-sm border border-blue-100">
                                                        {getPrice(g.action)} credits/{pricing.find(p => p.action === g.action)?.unitName || (g.id === "list" ? "fetch" : "search")}
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* STEP 2: Target */}
                                <div>
                                    <label className="text-sm font-bold text-gray-800 mb-3 block uppercase tracking-wide">
                                        Step 2 — Instagram Target
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">
                                            {isHashtag ? <LuHash size={20} /> : <LuSearch size={20} />}
                                        </div>
                                        <input type="text" placeholder="e.g. @username or #hashtag"
                                            className={`w-full pl-14 pr-5 py-4 rounded-2xl border-2 text-base font-medium text-gray-900 placeholder-gray-400 transition-all outline-none bg-gray-50/50
                                                ${errors.target ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                                                : targetValue ? "border-emerald-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                                : "border-gray-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100"}`}
                                            {...register("target", { required: "Target is required" })} />
                                        {targetValue && !errors.target && (
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2">
                                                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                                    <LuCheck size={14} className="text-emerald-700" strokeWidth={3} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {isHashtag && <p className="mt-2 text-[11px] text-fuchsia-600 font-bold flex items-center gap-1"><LuHash size={11} /> Hashtag mode active — users will be sourced from this tag's recent posts</p>}
                                    {errors.target && <p className="text-red-500 text-sm mt-2 font-medium">{errors.target.message}</p>}
                                </div>

                                {/* STEP 3: Source (only if not hashtag and not list-only goal) */}
                                {!isHashtag && (
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                                                Step 3 — Scrape Source
                                            </label>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100 px-2 py-0.5 rounded-full">(Optional)</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {(["followers", "following"] as const).map(type => (
                                                <label key={type} className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all
                                                    ${scrapeTypeValue === type ? "border-violet-500 bg-violet-50" : "border-gray-200 bg-gray-50/50 hover:border-gray-300"}`}>
                                                    <input type="radio" value={type} className="sr-only" {...register("scrapeType")} />
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors
                                                        ${scrapeTypeValue === type ? "bg-violet-100" : "bg-white border border-gray-200"}`}>
                                                        {type === "followers"
                                                            ? <LuUsers size={18} className={scrapeTypeValue === type ? "text-violet-600" : "text-gray-400"} />
                                                            : <LuUserPlus size={18} className={scrapeTypeValue === type ? "text-violet-600" : "text-gray-400"} />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className={`text-sm font-bold capitalize ${scrapeTypeValue === type ? "text-violet-700" : "text-gray-700"}`}>Target's {type}</p>
                                                        {!pricingLoading && <p className="text-[11px] text-gray-400 mt-0.5">{getPrice("list_fetch")} credits per fetch</p>}
                                                    </div>
                                                    {scrapeTypeValue === type && (
                                                        <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center shrink-0">
                                                            <LuCheck size={11} className="text-white" strokeWidth={3} />
                                                        </div>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                        {errors.scrapeType && <p className="text-red-500 text-sm mt-2 font-medium">{errors.scrapeType.message}</p>}
                                    </div>
                                )}

                                {/* STEP 4: Search Limit */}
                                <div>
                                    <div className="mb-3">
                                        <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                                            Step {isHashtag ? "3" : "4"} — {selectedGoal.limitLabel}
                                        </label>
                                        <p className="text-[11px] text-gray-400 mt-1">Credits are charged per <strong>search</strong>, not per result found.</p>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">
                                            {selectedGoal.icon}
                                        </div>
                                        <input type="number" min={1} placeholder={selectedGoal.limitPlaceholder}
                                            className={`w-full pl-14 pr-5 py-4 rounded-2xl border-2 text-base font-medium text-gray-900 placeholder-gray-400 transition-all outline-none bg-gray-50/50
                                                ${errors.resultLimit ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                                                : limitValue ? "border-emerald-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                                : "border-gray-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100"}`}
                                            {...register("resultLimit", { required: "This field is required", min: { value: 1, message: "Must be at least 1" } })} />
                                    </div>
                                    {errors.resultLimit && <p className="text-red-500 text-sm mt-2 font-medium">{errors.resultLimit.message}</p>}
                                </div>

                                {/* STEP 5: Location */}
                                <div>
                                    <label className="text-sm font-bold text-gray-800 mb-3 block uppercase tracking-wide">
                                        Step {isHashtag ? "4" : "5"} — Target Location / Proxy Group
                                    </label>
                                    <div className="space-y-2">
                                        <select
                                            className="w-full px-5 py-4 rounded-2xl border-2 border-gray-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none bg-gray-50/50 text-gray-700 font-medium transition-all"
                                            {...register("locationSelect")}
                                        >
                                            <option value="in">🇮🇳 India</option>
                                            <option value="us">🇺🇸 United States</option>
                                            <option value="uk">🇬🇧 United Kingdom</option>
                                            <option value="us">🌐 Other</option>
                                        </select>
                                        <p className="text-xs text-gray-500">
                                            Defaulted to your location (<span className="font-semibold uppercase">{detectedLocation}</span>). Change it to target profiles from another region.
                                        </p>
                                    </div>
                                </div>



                                {/* Submit */}
                                {user && user.creditsRemaining < 1 && !user?.isAdmin ? (
                                    <div className="space-y-2">
                                        <button type="button" onClick={() => navigate("/billing")}
                                            className="w-full py-4 rounded-2xl text-base font-bold bg-amber-500 text-white shadow-xl shadow-amber-200 hover:shadow-2xl hover:-translate-y-1 transition-all flex items-center justify-center gap-2">
                                            <LuZap size={20} className="fill-white" /> Recharge Credits to Start
                                        </button>
                                        <p className="text-center text-[10px] text-amber-600 font-black uppercase tracking-widest">You have 0 credits remaining.</p>
                                    </div>
                                ) : (
                                    <button type="submit" disabled={isSubmitting}
                                        className={`w-full py-4 rounded-2xl text-base font-bold transition-all duration-300 flex items-center justify-center gap-2
                                            ${isSubmitting ? "bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200"
                                            : "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-xl shadow-violet-200 hover:shadow-2xl hover:-translate-y-1 active:translate-y-0"}`}>
                                        {isSubmitting
                                            ? <><div className="w-5 h-5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />Starting...</>
                                            : <><LuZap size={20} className="fill-white/20" />Start Scraping</>}
                                    </button>
                                )}
                            </form>
                        </div>

                        {/* Feature chips */}
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                { icon: <LuZap size={18} className="text-violet-600" />, bg: "bg-violet-50", title: "Fast & Async", desc: "Jobs run in background" },
                                { icon: <LuShield size={18} className="text-emerald-600" />, bg: "bg-emerald-50", title: "Verified Emails", desc: "High-deliverability results" },
                                { icon: <LuClock size={18} className="text-amber-600" />, bg: "bg-amber-50", title: "Lifetime Storage", desc: "Export anytime from history" },
                            ].map(f => (
                                <div key={f.title} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                                    <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-3`}>{f.icon}</div>
                                    <h4 className="text-sm font-bold text-gray-900 mb-1">{f.title}</h4>
                                    <p className="text-xs text-gray-500">{f.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT — Marketing / Info */}
                    <div className="hidden lg:flex lg:col-span-5 flex-col gap-8 sticky top-6">
                        <div className="pr-2">
                            <span className="inline-block px-3 py-1 rounded-full bg-violet-100 text-violet-700 font-bold text-[10px] uppercase tracking-widest mb-4 border border-violet-200">Data Enrichment</span>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight tracking-tight">
                                Turn social audiences into{" "}
                                <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">qualified leads.</span>
                            </h3>
                            <p className="text-gray-500 font-medium leading-relaxed text-sm mb-6">
                                Don't waste hours manually collecting data. Our scraper intelligently grabs clean, verified public data directly from Instagram to fuel your outreach campaigns.
                            </p>
                            <ul className="space-y-4 mb-6">
                                {[
                                    "Bypass manual copy-pasting entirely",
                                    "Clean data ready for CSV or XLS export",
                                    "Zero manual proxies or code required",
                                    "Credits charged per search — transparent billing",
                                ].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-gray-800 font-semibold text-sm">
                                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 border border-emerald-200">
                                            <LuCheck className="text-emerald-700" size={14} strokeWidth={3} />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Browser mockup */}
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200/60 bg-white ml-2 rotate-1 hover:rotate-0 transition-transform duration-500">
                            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400" />
                                <div className="w-3 h-3 rounded-full bg-amber-400" />
                                <div className="w-3 h-3 rounded-full bg-emerald-400" />
                                <div className="ml-4 flex-1 h-5 bg-white rounded-md border border-gray-200/50 flex items-center px-3">
                                    <div className="w-32 h-1.5 bg-gray-200 rounded-full" />
                                </div>
                            </div>
                            <div className="p-5 bg-white">
                                <div className="flex justify-between items-center mb-5">
                                    <div className="flex items-center gap-2">
                                        <div className="h-5 w-5 bg-violet-100 rounded text-violet-500 flex items-center justify-center"><LuUsers size={12} /></div>
                                        <div className="h-4 w-24 bg-gray-900 rounded" />
                                    </div>
                                    <div className="h-8 w-28 bg-violet-600 rounded-lg flex items-center justify-center">
                                        <div className="h-2 w-12 bg-white/80 rounded-full" />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <div key={i} className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                                                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="avatar" className="w-full h-full object-cover" />
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="h-2.5 w-32 bg-gray-800 rounded" />
                                                    <div className="h-2 w-24 bg-gray-400 rounded" />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="h-2 w-32 bg-emerald-100 rounded" />
                                                <div className="h-5 w-16 bg-gray-100 rounded flex items-center justify-center">
                                                    <div className="h-1.5 w-8 bg-gray-400 rounded-full" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
